import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import DensityMediumRoundedIcon from "@mui/icons-material/DensityMediumRounded";
import { Storage } from "@utils";
import {
  Button,
  Text,
  DropDown,
  CartsView,
  AuthenticationModal,
} from "@components";
import c2hLogo from "@assets/images/c2hLogo.png";
import defaultUser from "@assets/images/defaultUser.png";
import { Dialog, DialogPanel } from "@headlessui/react";
import SearchBarHeader from "../SearchBarHeader";
import useScrollingHeader from "../../hooks/useScrollingHeader";
import { setLanguage } from "../../redux/user/slice";
import { arrangeNavData } from "../../utils/arrangeNavData";
import { change_Language } from "../../redux/settings/slice";
import { setIsMobileMenuOpen } from "../../redux/header/slice";
import "../../App.css";

const AuthenticationRoute = ({ onPress }) => {
  const { t } = useTranslation("common");
  return (
    <div
      onClick={onPress}
      className="w-[155px] h-[40px] rounded-full bg-buttonColor flex flex-row items-center justify-around px-6 hover:cursor-pointer hover:opacity-80"
    >
      <p className="text-buttonTextColor text-center font-extrabold">
        {t("header.agentLogin")}
      </p>
    </div>
  );
};

const Accordion = ({ value, active, path, inActive, index, data }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex flex-col w-[100%]">
      <div className="flex  w-full items-center cursor-pointer relative">
        <TopNavBar
          key={index}
          value={value}
          path={path}
          active={active}
          inActive={inActive}
        />
        {data?.length ? (
          <div className="absolute right-0">
            {open ? (
              <ExpandLessIcon
                sx={{ color: "white" }}
                onClick={() => setOpen(!open)}
              />
            ) : (
              <ExpandMoreIcon
                sx={{ color: "white" }}
                onClick={() => setOpen(!open)}
              />
            )}
          </div>
        ) : null}
      </div>
      {open && data?.length && (
        <div className={` w-full flex flex-col pr-5`} key={index}>
          {data?.map((item, ind) => {
            return (
              <div key={ind}>
                <p
                  className={`${
                    ind === 0 ? "" : "mt-3"
                  } block  pb-1 text-[16px] font-semibold text-gray-400 ml-2`}
                >
                  {item?.attributes?.title}
                </p>
                {item?.attributes?.child?.data?.map((value, i) => {
                  return (
                    <NavLink
                      to={value?.attributes?.url}
                      key={i}
                      className="my-1.5 block text-[14px] font-semibold text-gray-500 hover:text-gray-400 ml-4 "
                    >
                      {value?.attributes?.title}
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const OpenProfile = (props) => {
  const { t } = useTranslation("common");

  const options = [
    {
      index: 0,
      title: t("profile.profile"),
      value: "/profile",
    },
    {
      index: 1,
      title: t("profile.setting"),
      value: "/settings",
    },
    {
      index: 2,
      title: t("profile.logOut"),
      value: "/login",
    },
  ];

  const { userDetail } = useSelector((store) => store.authReducer);

  let fname = "User";
  let image = defaultUser;

  if (userDetail) {
    fname = userDetail?.firstname;
    image = userDetail?.image;
  }

  return (
    <div className="flex flex-row items-center pr-6 text-white ">
      <DropDown
        title={fname}
        options={options}
        titleStyle="max-w-[150px] truncate"
        image={image}
      />
    </div>
  );
};

const TopNavBar = ({ value, active, path, inActive }) => {
  const pathName = "/" + window.location.pathname.split("/")[1];
  return (
    <div className="w-full">
      <NavLink
        key={value}
        to={path}
        className={`${
          (pathName === "/course" ? "/courses" : pathName) === path
            ? active
            : inActive
        }  ${
          (pathName === "/course" ? "/courses" : pathName) !== path &&
          "header-underline-animation after:bg-[white] xl2:after:bg-white h-[100%] "
        }`}
      >
        {value}
      </NavLink>
    </div>
  );
};

export default function Header({
  hideHeaderSearchBar,
  searchInputProps,
  searchMenuProps,
}) {
  const headerClassName = useScrollingHeader();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { i18n, t } = useTranslation("common");
  const { menus, logo, ismobileMenuOpen } = useSelector(
    (state) => state.headerReducer
  );
  const { userDetail, userToken } = useSelector((store) => store.authReducer);
  const { language } = useSelector((store) => store.userReducer);
  const { staticPageData } = useSelector((store) => store.settingsReducer);
  const logoPath = logo?.data && logo?.data?.logo?.url;

  const headerNav = menus?.data?.filter(
    (val, ind) => val?.slug === "header-nav"
  );
  const navlinks = headerNav && headerNav[0]?.items;

  const [imageExists, setImageExists] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [announcementTxt, setAnnouncementTxt] = useState("");
  const [searchVisible, setSearchVisible] = useState(false);
  const [authModalVisible, setAuthModalVisible] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const [state, setState] = useState({
    show: false,
  });

  const agentLoginData = staticPageData?.data?.find(
    (item) => item?.slug === "agent-login"
  );

  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      setIsScrolled(offset > 50); // change this value as needed
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const checkImageExists = () => {
    const img = new Image();
    img.src = logoPath;

    img.onload = () => {
      setImageExists(true);
    };

    img.onerror = () => {
      setImageExists(false);
    };
  };

  React.useEffect(() => {
    checkImageExists();
  }, [logoPath]);

  useEffect(() => {
    const announcementTxt = logo?.data?.announcementText;
    if (announcementTxt) {
      setAnnouncementTxt(announcementTxt);
    }
  }, [logo]);

  useEffect(() => {
    dispatch(setIsMobileMenuOpen(mobileMenuOpen));
  }, [mobileMenuOpen]);
  let yscroll = 0;

  useEffect(() => {
    if (window.screen.width >= 600) {
      window.addEventListener("scroll", function () {
        setSearchVisible(window.scrollY > (announcementTxt ? 400 : 350));
        if (
          (window.scrollY - yscroll < 0
            ? (window.scrollY - yscroll) * -1
            : window.scrollY - yscroll) > 300
        ) {
          setState((prev) => ({
            ...prev,
            show: false,
          }));
          yscroll = window.scrollY;
        }
      });
    }
  }, []);

  const onPressLogin = () => navigate("/login");

  const onPressSignup = (e) => {
    e?.stopPropagation();
    navigate("/signup");
  };

  const handleLanguageChange = (props) => {
    dispatch(setLanguage(props));
    i18n.changeLanguage(props.language);
    Storage.save(Storage.LANGUAGE, props.language);
    dispatch(change_Language(props.language));
  };

  const showSuggestionBox = (e) => {
    e.stopPropagation();
    !state.show && setState((prev) => ({ ...prev, show: true }));
  };

  const hideSuggestionBox = () =>
    state.show && setState((prev) => ({ ...prev, show: false }));

  const setData = (ele, ind) => {
    const chunks = [];
    const childData = ele?.attributes?.child?.data;
    for (let i = 0; i < childData.length; i += 4) {
      chunks.push(childData.slice(i, i + 4));
    }
    return (
      <>
        {chunks.map((chunk, chunkIndex) => (
          <div
            className="border-r-[1px] border-buttonColor pr-10 w-[25%]"
            key={chunkIndex}
          >
            <p className="mb-10 xl2:mb-4 block pb-1 text-[18px] font-blackText text-buttonTextColor md:mx-2 h-[18px]">
              {chunkIndex === 0 ? ele?.attributes?.title : ""}
            </p>
            {chunk.map((val, index) => (
              <NavLink
                key={index}
                to={val?.attributes?.url}
                className="my-5 block text-[16px] font-medium opacity-80 text-buttonTextColor hover:opacity-100 md:mx-2 cursor-pointer hover:underline underline-offset-2"
              >
                {val?.attributes?.title}
              </NavLink>
            ))}
          </div>
        ))}
      </>
    );
  };

  const onPressBook = () => navigate("/book");

  const BookButton = () => {
    return (
      <div
        onClick={onPressBook}
        className="w-[160px] h-[40px] rounded-full bg-buttonColor flex flex-row items-center justify-around px-10 hover:cursor-pointer hover:opacity-80"
      >
        <p className="text-buttonTextColor text-center font-extrabold">
          {t("header.book")}
        </p>
      </div>
    );
  };

  return (
    <div
      className={`w-full h-auto flex flex-col fixed top-0 z-50 bg-transparent`}
    >
      {/* <nav>
        {announcementTxt && !ismobileMenuOpen && (
          <div
            className={`w-full  ${
              isScrolled ? "bg-white" : "bg-primary"
            } flex justify-center py-[6px]`}
          >
            <button
              className={`font-blackText underline text-center underlin underline-offset-2  hover:drop-shadow-lg hover:shadow-lg hover:opacity-90  ${
                isScrolled ? "text-primary" : "text-buttonTextColor"
              }`}
              onClick={() => {
                navigate("/courses");
              }}
            >
              {announcementTxt}
            </button>
          </div>
        )}
      </nav> */}

      {authModalVisible && (
        <AuthenticationModal
          agentLoginData={agentLoginData}
          onPressClose={() => setAuthModalVisible(false)}
        />
      )}

      <div
        className={`w-full h-auto  ${
          isScrolled ? "bg-white" : "bg-transparent"
        } flex flex-row items-center justify-between lg2:px-20 sm:px-4 px-3 md:gap-x-10 gap-x-2 pt-4 pb-4`}
      >
        <div
          className={`justify-center w-auto md:max-w-[250px] max-w-[150px] md:min-w-[200px] min-w-[150px] min-h-[60px] max-h-[150px] h-auto md:py-0 py-4 xl2:hidden flex ${
            ismobileMenuOpen ? "hidden" : "flex"
          }`}
        >
          <NavLink
            to="https://golfincostabrava.org/en/"
            className="webkitHighlight"
          >
            <img
              className="w-full h-auto max-h-[150px]"
              src={imageExists ? logoPath : c2hLogo}
              alt=""
            />
          </NavLink>
        </div>

        <nav className={`${navbarView} xl2:flex hidden`} aria-label="Global">
          <div className={`container sm:mx-auto ${LgNavStyle}`}>
            <div
              className={`justify-center w-auto min-w-[200px] max-w-[250px] max-h-[150px] h-auto md:py-0 py-4${
                ismobileMenuOpen ? "hidden" : "flex"
              }`}
            >
              <NavLink
                to="https://golfincostabrava.org/en/"
                className="webkitHighlight"
              >
                <img
                  className="w-full h-auto max-h-[150px]"
                  src={imageExists ? logoPath : c2hLogo}
                  alt=""
                />
              </NavLink>
            </div>

            <div className="flex flex-row items-center justify-start w-full pl-10">
              {navlinks && navlinks?.length > 0 ? (
                navlinks?.map((item, index) => (
                  <div className="group h-[100%]" key={index}>
                    <div className="flex items-center justify-between menu-hover pr-[70px] py-[4px] h-[100%]">
                      <TopNavBar
                        key={index}
                        value={item?.title}
                        path={item?.url}
                        active={`${ActiveTab} ${
                          isScrolled ? "text-blackText" : "text-buttonTextColor"
                        }`}
                        inActive={`${InactiveTab} ${
                          isScrolled ? "text-blackText" : "text-buttonTextColor"
                        }`}
                      />
                    </div>
                    {item?.child?.length ? (
                      <div
                        className="invisible absolute z-50 flex bg-white text-gray-800 shadow-xl group-hover:visible left-0 w-[100vw] pt-[15px]"
                        key={index}
                      >
                        <div className="xl2:px-20 lg:px-10 border-t-[1px] border-buttonTextColor py-[50px] flex space-between w-full">
                          <div className={`w-full flex pr-5`}>
                            {item?.child?.map((ele, ind) => {
                              return setData(ele, ind);
                            })}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <></>
                    )}
                  </div>
                ))
              ) : (
                <></>
              )}
            </div>

            <div className="flex flex-row items-center justify-end gap-x-4">
              <CartsView />
              <BookButton />
              <AuthenticationRoute onPress={() => setAuthModalVisible(true)} />
            </div>
          </div>
        </nav>

        <div
          className={`xl2:hidden flex flex-row items-end justify-center gap-x-2 ${
            ismobileMenuOpen ? "hidden" : "flex"
          }`}
        >
          <CartsView />

          <Button
            style={{ backgroundColor: "rgba(18,16,55,0.30)" }}
            variant="primary"
            className="rounded-md text-white h-10 w-10 webkitHighlight"
            onClick={() => {
              dispatch(setIsMobileMenuOpen(true));
              setMobileMenuOpen(true);
            }}
          >
            <DensityMediumRoundedIcon aria-hidden="true" />
          </Button>
        </div>
      </div>

      <Dialog
        as="div"
        className="xl2:hidden flex"
        open={ismobileMenuOpen}
        onClose={setMobileMenuOpen}
      >
        <div className="fixed inset-0 z-[9]" />
        <DialogPanel className={SmDrawer}>
          <div className="flex items-center justify-between xl2:mt-16 lg2:mt-16 lg:mt-16 mt-4">
            <div className="flex justify-center webkitHighlight">
              <AuthenticationRoute onPress={() => setAuthModalVisible(true)} />
            </div>

            <Button
              className="-m-2.5 rounded-md p-2.5 text-gray-700 webkitHighlight"
              onClick={() => {
                dispatch(setIsMobileMenuOpen(false));
                setMobileMenuOpen(false);
              }}
              variant="primary"
            >
              <ClearRoundedIcon
                className="h-6 w-6 text-buttonTextColor"
                aria-hidden="true"
                fontSize="large"
              />
            </Button>
          </div>
          <div className="w-full flex flex-col items-start gap-y-4 pt-8 justify-start">
            <BookButton />
          </div>

          {/* <div className="mt-7">
             <ChangeLanguage
               title={language}
               titleStyle="max-w-[150px] truncate"
               handleLanguageChange={handleLanguageChange}
             />
           </div> */}
          <div className="mt-6 flow-root">
            <div className="-my-6 divide-y divide-gray-500/10">
              <div className="space-y-2 py-6">
                {navlinks && navlinks?.length > 0 ? (
                  navlinks?.map((item, index) => (
                    <Accordion
                      key={index}
                      value={item?.title}
                      path={item?.url}
                      active={MobActive}
                      inActive={MobInactive}
                      data={item?.children}
                    />
                  ))
                ) : (
                  <></>
                )}
              </div>
            </div>
          </div>
        </DialogPanel>
      </Dialog>
    </div>
  );
}
// invisible
const navbarView = `
w-full 
items-center 
justify-between 
xl2:py-3 py-0
`;

const LargeDeviceContainer = `
flex 
w-full 
items-center 
justify-between 
xl:py-3 py-0 
gap-x-1 
`;

const LgNavStyle = `
xl2:flex
flex-row
justify-between
items-center
h-[100%]
px-0 
w-full
`;

const ActiveTab = `
text-[17px] 
font-sans
font-blackText
font-semibold
underline
underline-offset-4
decoration-1
self-center
hover:cursor-default
 `;

const InactiveTab = `
font-sans
text-[17px] 

self-center
font-semibold`;

const AuthActive = `
lg:px-4 
px-2 
py-[8px] 
bg-primary 
rounded-full 
flex 
items-center 
justify-center`;

const AuthInactive = `
lg:px-4 
px-2 
py-[8px] 
bg-buttonTextColor 
rounded-full 
flex 
items-center 
justify-center`;

const SmDrawer = `
fixed 
inset-y-0 
right-0 
z-10 
w-full 
overflow-y-auto 
bg-[#6B8796]
px-6 
py-6 
sm:max-w-sm 
sm:ring-1 sm:ring-gray-900/10`;

const MobActive = `
text-[18px] 
-mx-3 
block 
rounded-lg 
px-3 
py-2 
font-sans
text-base 
font-bold 
leading-7 
text-buttonColor 
border-b-2 
border-b-buttonColor
`;

const MobInactive = `
text-[18px] 
-mx-3 
font-sans
block 
rounded-lg 
px-3 
py-2 
text-base 
font-semibold 
leading-7 
text-buttonTextColor
`;

const AuthText = `
text-buttonTextColor 
text-[17px] 
font-bold 
font-sans
`;

const InactiveText = `
text-[#004996] 
text-[17px] 
font-bold 
font-sans
`;
